import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function Alumni() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [pick, setPick] = useState('');
  const { data: alumni } = useQuery({ queryKey: ['alumni'], queryFn: () => api.get('/alumni').then((r) => r.data) });
  const { data: students } = useQuery({ queryKey: ['students'], queryFn: () => api.get('/students').then((r) => r.data) });
  const inval = () => { qc.invalidateQueries({ queryKey: ['alumni'] }); qc.invalidateQueries({ queryKey: ['students'] }); };
  const convertMut = useMutation({
    mutationFn: (sid) => api.post('/alumni/convert', { student_id: sid }),
    onSuccess: () => { toast.success('Converted to alumni'); setPick(''); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const profileMut = useMutation({
    mutationFn: ({ sid, patch }) => api.patch(`/alumni/${sid}/profile`, patch),
    onSuccess: () => { toast.success('Alumni profile updated'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const revertMut = useMutation({
    mutationFn: (sid) => api.delete(`/alumni/${sid}`),
    onSuccess: () => { toast.success('Returned to active roll'); inval(); }, onError: (e) => toast.error(errMsg(e)),
  });
  const editProfile = (a) => {
    const occupation = window.prompt('Occupation:', a.occupation || '') ?? a.occupation;
    const company = window.prompt('Company / University:', a.company || '') ?? a.company;
    const city = window.prompt('City:', a.city || '') ?? a.city;
    const phone = window.prompt('Phone:', a.phone || '') ?? a.phone;
    profileMut.mutate({ sid: a.student_id, patch: { occupation, company, city, phone } });
  };
  const filtered = (alumni || []).filter((a) => !q || `${a.name} ${a.company} ${a.city} ${a.occupation}`.toLowerCase().includes(q.toLowerCase()));
  const grade12 = (students || []).filter((s) => s.grade === '12' && s.status === 'active');
  const convertible = (students || []).filter((s) => s.status === 'active');

  return (
    <div data-testid="alumni-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Alumni</h1>
        <div className="flex gap-2 items-center">
          <select data-testid="alumni-convert-select" value={pick} onChange={(e) => setPick(e.target.value)}
            className="border border-slate-200 rounded-md px-2 py-2 text-xs bg-white max-w-[260px]">
            <option value="">Pick a student to convert...</option>
            {convertible.map((s) => <option key={s.id} value={s.id}>{`${s.name} (${s.admission_no || 'no adm no'} · Grade ${s.grade})`}</option>)}
          </select>
          <button data-testid="alumni-convert-button" disabled={!pick || convertMut.isPending} onClick={() => convertMut.mutate(pick)}
            className="text-xs bg-slate-900 text-white rounded px-3 py-2 hover:bg-slate-800 disabled:opacity-40">Convert to alumni</button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-6">Graduate registry with contact tracking · Grade 12 auto-converts on year rollover ({grade12.length} currently in grade 12)</p>

      <Input data-testid="alumni-search-input" placeholder="Search name, company, city..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm mb-4 bg-white" />

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="alumni-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            {['Name', 'Batch', 'Occupation', 'Company', 'City', 'Contact', 'Actions'].map((h) => <th key={h} className="py-2 px-3 font-mono-label text-[10px] uppercase text-slate-400">{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map((a, i) => (
              <tr key={a.student_id} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/50' : ''}`} data-testid={`alumni-row-${a.student_id}`}>
                <td className="py-2 px-3 font-medium">{a.name}</td>
                <td className="py-2 px-3">{a.graduation_year || '-'}</td>
                <td className="py-2 px-3">{a.occupation || '-'}</td>
                <td className="py-2 px-3">{a.company || '-'}</td>
                <td className="py-2 px-3">{a.city || '-'}</td>
                <td className="py-2 px-3 text-xs">{a.phone || a.email || '-'}</td>
                <td className="py-2 px-3">
                  <button data-testid={`alumni-edit-${a.student_id}`} onClick={() => editProfile(a)} className="text-xs underline text-orange-700 me-2">edit profile</button>
                  <button data-testid={`alumni-revert-${a.student_id}`} onClick={() => window.confirm(`Return ${a.name} to active students?`) && revertMut.mutate(a.student_id)} className="text-xs underline text-slate-400">revert</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No alumni yet - convert a student or run a year rollover.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
